import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { InferType, object, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { t } from "i18next";
import { tv } from "tailwind-variants";
import { H3, P } from "@/components/Typography";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import {
  useConsentFormFields,
  usePostPrivacyPolicyUrl,
} from "@/requests/hooks/collect.ts";

const styles = tv({
  slots: {
    titleAndDescriptionContainer: "mb-20",
    title: "mb-10 font-bold",
    buttonContainer: "my-20 md:my-45",
  },
});

declare interface IProps {
  privacyPolicyUrl?: string;
  version?: number;
  isDataFetching: boolean;
  isPostPrivacyEmailPending: boolean;
  postPrivacyPolicyUrlHook: ReturnType<typeof usePostPrivacyPolicyUrl>;
  refetchConsentFormFields: ReturnType<typeof useConsentFormFields>["refetch"];
}

const PrivacyPolicyUrlForm = ({
  privacyPolicyUrl,
  version,
  isDataFetching,
  isPostPrivacyEmailPending,
  postPrivacyPolicyUrlHook,
  refetchConsentFormFields,
}: IProps) => {
  const { titleAndDescriptionContainer, title, buttonContainer } = styles();

  const formSchema = object({
    url: string()
      .url()
      .required(t("settings.widgetSettings.privacyDetails.policy.error")),
  });

  type IPrivacyPolicyUrlForm = InferType<typeof formSchema>;

  const {
    mutate: postPrivacyPolicyUrl,
    isPending: isPostPrivacyPolicyUrlPending,
    isSuccess: isPostPrivacyPolicyUrlSuccess,
    data: postPrivacyPolicyUrlData,
  } = postPrivacyPolicyUrlHook;

  const {
    watch,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<IPrivacyPolicyUrlForm>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      url: privacyPolicyUrl ?? undefined,
    },
  });

  const handleOnSubmitPostPrivacyPolicyUrl = ({
    url,
  }: IPrivacyPolicyUrlForm) => {
    postPrivacyPolicyUrl({
      privacyPolicyUrl: url,
      version: Math.max(postPrivacyPolicyUrlData?.version ?? 0, version ?? 0),
    });
  };

  useEffect(() => {
    if (isPostPrivacyPolicyUrlSuccess) {
      toast.success(t("settings.widgetSettings.privacyDetails.policy.success"));
      refetchConsentFormFields();
    }
  }, [isPostPrivacyPolicyUrlSuccess]);

  const shouldButtonBeDisabled = () =>
    isDataFetching ||
    isPostPrivacyPolicyUrlPending ||
    isPostPrivacyEmailPending ||
    (postPrivacyPolicyUrlData?.privacyPolicyUrl ?? privacyPolicyUrl) ===
      watch("url");

  const renderTitleAndDescriptionSection = () => (
    <div className={titleAndDescriptionContainer()}>
      <div className={title()}>
        <H3>{t("settings.widgetSettings.privacyDetails.policy.title")}</H3>
      </div>
      <P>{t("settings.widgetSettings.privacyDetails.policy.description")}</P>
    </div>
  );

  const renderPrivacyPolicyUrlInput = () => (
    <div>
      <Input
        label={t("settings.widgetSettings.privacyDetails.policy.formLabel")}
        placeholder={t(
          "settings.widgetSettings.privacyDetails.policy.placeholder",
        )}
        type="url"
        role="textbox"
        {...register("url")}
        helperText={errors?.url?.message}
        variant={!!errors?.url?.message ? "error" : undefined}
        helperTextTestId="privacy-policy-input"
        testId="privacy-policy-input"
        id="privacy-policy-input"
      />
    </div>
  );

  const renderButton = () => (
    <div className={buttonContainer()}>
      <Button
        type="submit"
        loading={isPostPrivacyPolicyUrlPending}
        trackingId="control-panel.collect.widget-settings.privacy-details.update-url"
        disabled={shouldButtonBeDisabled()}
        testId="privacy-policy-cta"
        id="privacy-policy-cta">
        {t("settings.widgetSettings.privacyDetails.policy.cta")}
      </Button>
    </div>
  );

  return (
    <form onSubmit={handleSubmit(handleOnSubmitPostPrivacyPolicyUrl)}>
      {renderTitleAndDescriptionSection()}
      {renderPrivacyPolicyUrlInput()}
      {renderButton()}
    </form>
  );
};

export default PrivacyPolicyUrlForm;
